import { writeFile } from 'node:fs/promises'
import { join, relative } from 'node:path'

export type ExportReadmeKind =
  | 'run-diagnostic'
  | 'message-diagnostic'
  | 'thread-export'
  | 'project-conversation'
  | 'project-inspect'
  | 'project-export'
  | 'artifact-export'
  | 'analysis-inspect'
  | 'analysis-export'

export type ExportReadmeInput = {
  kind: ExportReadmeKind
  targetId: string
  destination: string
  files: string[]
  messageId?: string
  projectId?: string
  threadId?: string
  runId?: string
  analysisId?: string
  rawIncluded?: boolean
  snapshotFidelity?: string
}

const EXPORT_DESCRIPTIONS: Record<ExportReadmeKind, { title: string; description: string }> = {
  'run-diagnostic': {
    title: '精确运行诊断',
    description: '按唯一 run ID 导出的运行快照、事件、工具证据和产物关联。',
  },
  'message-diagnostic': {
    title: '单条消息诊断',
    description: '以指定 Assistant 消息为终点导出的完整单轮问答窗口和工具调用证据。',
  },
  'thread-export': {
    title: '完整对话线程',
    description: '按 thread ID 导出的完整消息序列和工具调用记录。',
  },
  'project-conversation': {
    title: '项目对话导出',
    description: '项目下全部对话线程、消息和工具调用的汇总证据。',
  },
  'project-inspect': {
    title: '项目诊断概览',
    description: '项目状态、产物和近期运行的有界机器可读概览。',
  },
  'project-export': {
    title: '项目完整导出',
    description: '项目清单、运行证据以及当前产物正文的本地副本。',
  },
  'artifact-export': {
    title: '创作产物导出',
    description: '指定项目阶段的产物元数据和可读取正文。',
  },
  'analysis-inspect': {
    title: '爆款分析概览',
    description: '指定爆款分析的状态、摘要和关联产物概览。',
  },
  'analysis-export': {
    title: '爆款分析完整导出',
    description: '指定爆款分析的机器可读主记录和已生成产品正文。',
  },
}

const KNOWN_FILE_DESCRIPTIONS: Record<string, string> = {
  'diagnostic.json': '运行诊断的机器可读主记录；产物正文拆分保存在 artifacts/。',
  'message.json': '单轮问答的机器可读主记录，包含上下文、轮次窗口、trace 和工具证据。',
  'tool-calls.json': '从单轮主记录提取的工具调用投影，便于 Agent 直接遍历。',
  'user-message.md': '本轮用户请求的纯文本阅读副本。',
  'assistant-message.md': '本轮最终 Assistant 回复的纯文本阅读副本，不代表完整轮次。',
  'thread.json': '完整线程的机器可读主记录。',
  'project-conversation.json': '项目全部对话的机器可读主记录。',
  'project-inspect.json': '项目诊断概览的机器可读主记录。',
  'project-export.json': '项目完整导出的机器可读清单和证据索引。',
  'analysis-inspect.json': '爆款分析概览的机器可读主记录。',
  'analysis.json': '爆款分析完整导出的机器可读主记录。',
}

function markdownCode(value: string): string {
  return `\`${value.replaceAll('`', '\\`')}\``
}

function portableRelativePath(destination: string, file: string): string {
  return relative(destination, file).replaceAll('\\', '/')
}

function describeFile(path: string): string {
  const filename = path.split('/').at(-1) ?? path
  const known = KNOWN_FILE_DESCRIPTIONS[filename]
  if (known) return known
  if (path.startsWith('artifacts/') && path.endsWith('.json')) return '创作产物的机器可读元数据。'
  if (path.startsWith('artifacts/') && path.endsWith('.md')) return '当前创作产物正文的 Markdown 阅读副本。'
  if (path.startsWith('products/') && path.endsWith('.md')) return '爆款分析产品正文的 Markdown 阅读副本。'
  if (path.endsWith('.json')) return '本次导出的机器可读附属记录。'
  if (path.endsWith('.md')) return '本次导出的 Markdown 阅读副本。'
  return '本次导出的附属文件。'
}

function snapshotDescription(snapshotFidelity: string | undefined): string {
  if (snapshotFidelity === 'exact') return '`exact`：消息边界与 QA run 快照具有精确关联。'
  if (snapshotFidelity === 'partial') return '`partial`：历史记录缺少精确 QA run 快照锚点；已有消息和工具数据仍可使用，但不能视为完整运行快照。'
  if (snapshotFidelity) return `${markdownCode(snapshotFidelity)}：服务端返回的快照完整度。`
  return '不适用：该导出类型未提供运行快照完整度。'
}

export function renderExportReadme(input: ExportReadmeInput): string {
  const exportDescription = EXPORT_DESCRIPTIONS[input.kind]
  const files = input.files.map(file => portableRelativePath(input.destination, file))
  const identities = [
    ['目标 ID', input.targetId],
    ['消息 ID', input.messageId],
    ['项目 ID', input.projectId],
    ['线程 ID', input.threadId],
    ['运行 ID', input.runId],
    ['分析 ID', input.analysisId],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]))
  const rawDescription = input.rawIncluded === true
    ? '已包含服务端允许导出的原始消息 parts。'
    : input.rawIncluded === false
      ? '未包含原始消息 parts；需要时使用 `--include-raw` 重新导出。'
      : '不适用：该导出类型未声明原始消息范围。'

  const lines = [
    '# Novel CLI 导出说明',
    '',
    `> **${exportDescription.title}**：${exportDescription.description}`,
    '',
    '## 导出身份',
    '',
    '| 字段 | 值 |',
    '| --- | --- |',
    ...identities.map(([label, value]) => `| ${label} | ${markdownCode(value)} |`),
    '',
    '## 数据完整度',
    '',
    `- 原始数据：${rawDescription}`,
    `- 运行快照：${snapshotDescription(input.snapshotFidelity)}`,
    '- JSON 是机器读取的事实源；Markdown 文件只是方便阅读的内容投影。',
    '',
    '## 推荐阅读顺序',
    '',
    '1. `README.md`：先确认导出范围、完整度和文件边界。',
    ...files.map((file, index) => `${index + 2}. ${markdownCode(file)}：${describeFile(file)}`),
    '',
    '## 文件清单',
    '',
    '| 文件 | 用途 |',
    '| --- | --- |',
    '| `README.md` | 当前导出目录的说明和阅读入口。 |',
    ...files.map(file => `| ${markdownCode(file)} | ${describeFile(file)} |`),
    '',
    '## 数据边界',
    '',
    '- 该目录可能包含未发布正文、用户输入和工具调用参数，请按内部诊断资料管理。',
    '- CLI 不会把登录凭据写入此说明；分享前仍应检查业务内容是否允许外发。',
    '- `partial` 不是导出失败，而是明确声明历史运行快照不完整。',
    '',
  ]

  return lines.join('\n')
}

export async function writeExportReadme(input: ExportReadmeInput): Promise<string> {
  const file = join(input.destination, 'README.md')
  await writeFile(file, renderExportReadme(input), 'utf8')
  return file
}
